import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import { request } from '../api/request';

/** 高级脚本（极客模式）空编辑器时的默认模板（与产品文档一致） */
const GEEK_DEFAULT_UPLINK_JS = `/**
 * 【上行解析脚本 Uplink JS】
 * 功能：将异构设备的原始报文，翻译为平台标准格式
 * @param {object|string} raw - 设备发来的原始数据
 * @returns {object} - 必须包含 nats_subject 和标准业务字段
 */
function parseUplink(raw) {
    // 1. 提取设备唯一标识 (SN)
    let sn = raw.device_id || "unknown_device";

    // 2. 组装标准返回结构
    return {
        nats_subject: "v1.robot.status." + sn, // 目标 NATS 路由
        source_vendor: "my_vendor",
        status_data: {
            power: raw.battery || 0,     // 将电池电量映射为 power
            speed: raw.velocity || 0.0   // 将速度映射为 speed
        },
        raw_payload: raw // 调试期可保留原始数据
    };
}`;

const GEEK_DEFAULT_DOWNLINK_JS = `/**
 * 【下行构建脚本 Downlink JS】
 * 功能：将 NATS 下发的标准指令，翻译为设备的方言报文
 * @param {object} raw - NATS 发来的标准对象 (如 {"cmd": "stop", "target": "car_01"})
 * @returns {object} - MQTT 需返回 {topic, payload}；WS 需直接返回 payload 对象
 */
function buildDownlink(raw) {
    return {
        topic: "robot/cmd/" + raw.target,
        payload: JSON.stringify({ action: raw.cmd })
    };
}`;

interface MappingRuleRow {
  source: string;
  target: string;
  desc: string;
}

interface TestKVRow {
  key: string;
  value: string;
}

/** 与后端 model.ProtocolScript JSON 字段对齐 */
interface StandardFieldPayload {
  message_name?: string;
  field_name: string;
  field_type: string;
  description: string;
  proto_tag: number;
}

interface ProtocolScriptPayload {
  id?: number;
  vendor_code: string;
  uplink_protocol: string;
  downlink_protocol: string;
  connection_config: string;
  network_mode?: string;
  uplink_js: string;
  downlink_js: string;
  created_at?: string;
  updated_at?: string;
}

@customElement('protocol-adapter-view')
export class ProtocolAdapterView extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
        sans-serif;
    }

    .page-wrap {
      display: grid;
      grid-template-columns: 15rem minmax(0, 1fr) minmax(0, 1fr);
      gap: 1.5rem;
      align-items: start;
    }

    @media (max-width: 1100px) {
      .page-wrap {
        grid-template-columns: 1fr;
      }
    }

    .sidebar {
      padding: 1rem 0.75rem;
    }

    .sidebar-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-bottom: 0.65rem;
      padding: 0 0.25rem;
    }

    .sidebar-title {
      margin: 0;
      font-size: 0.8125rem;
      font-weight: 600;
      color: #486581;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      flex: 1 1 auto;
      min-width: 0;
    }

    .btn-new {
      flex: 0 0 auto;
      border: none;
      background: #2684ff;
      color: white;
      font-size: 0.75rem;
      font-weight: 600;
      padding: 0.4rem 0.65rem;
      border-radius: 6px;
      cursor: pointer;
      white-space: nowrap;
      transition: background 0.15s;
    }

    .btn-new:hover {
      background: #005cc5;
    }

    .sidebar-list {
      list-style: none;
      margin: 0;
      padding: 0;
      max-height: min(70vh, 520px);
      overflow-y: auto;
    }

    .sidebar-item {
      margin: 0;
      padding: 0;
    }

    .sidebar-link {
      display: block;
      width: 100%;
      text-align: left;
      border: none;
      background: transparent;
      cursor: pointer;
      border-radius: 8px;
      padding: 0.65rem 0.75rem;
      font-size: 0.8125rem;
      color: #334e68;
      transition: background 0.15s, color 0.15s;
      border: 1px solid transparent;
    }

    .sidebar-link:hover {
      background: #f0f4f8;
      color: #102a43;
    }

    .sidebar-link.active {
      background: #e3f2ff;
      color: #0f172a;
      border-color: #90cdf4;
      font-weight: 600;
    }

    .sidebar-vendor {
      display: block;
      font-weight: 600;
      color: inherit;
    }

    .sidebar-proto {
      display: block;
      font-size: 0.75rem;
      color: #627d98;
      margin-top: 0.2rem;
    }

    .sidebar-link.active .sidebar-proto {
      color: #486581;
    }

    .sidebar-empty {
      font-size: 0.8125rem;
      color: #829ab1;
      padding: 0.5rem 0.75rem;
    }

    .main-pair {
      display: contents;
    }

    @media (max-width: 1100px) {
      .main-pair {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1.5rem;
      }
    }

    @media (max-width: 720px) {
      .main-pair {
        grid-template-columns: 1fr;
      }
    }

    .panel {
      background: white;
      border-radius: 8px;
      padding: 1.25rem 1.5rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
      border: 1px solid #e1e4e8;
    }

    .panel h2 {
      margin: 0 0 1rem;
      font-size: 1rem;
      font-weight: 600;
      color: #102a43;
      padding-bottom: 0.75rem;
      border-bottom: 1px solid #e1e4e8;
    }

    .field {
      margin-bottom: 1rem;
    }

    .field:last-of-type {
      margin-bottom: 0;
    }

    .field-row {
      display: flex;
      align-items: flex-end;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .field-row .field-grow {
      flex: 1 1 180px;
      min-width: 0;
      margin-bottom: 0;
    }

    label {
      display: block;
      margin-bottom: 0.35rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: #334e68;
    }

    input[type='text'],
    select,
    textarea {
      width: 100%;
      box-sizing: border-box;
      padding: 0.5rem 0.65rem;
      border-radius: 6px;
      border: 1px solid #bcccdc;
      font-size: 0.875rem;
      outline: none;
      transition: border-color 0.2s;
    }

    input:focus,
    select:focus,
    textarea:focus {
      border-color: #2684ff;
    }

    textarea {
      min-height: 120px;
      resize: vertical;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.8125rem;
      line-height: 1.45;
    }

    textarea.script-area {
      min-height: 160px;
    }

    textarea.payload-area {
      min-height: 100px;
    }

    .actions {
      margin: 1rem 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
    }

    .btn-run,
    .btn-secondary,
    .btn-outline {
      border: none;
      padding: 0.55rem 1.1rem;
      border-radius: 6px;
      font-weight: 500;
      font-size: 0.875rem;
      cursor: pointer;
      transition: background-color 0.2s, border-color 0.2s, color 0.2s;
    }

    .btn-run {
      background-color: #2684ff;
      color: white;
    }

    .btn-run:hover:not(:disabled) {
      background-color: #005cc5;
    }

    button.primary,
    .primary.btn-run {
      background-color: #2684ff;
      color: white;
    }

    button.primary:hover:not(:disabled),
    .primary.btn-run:hover:not(:disabled) {
      background-color: #005cc5;
    }

    .mapping-kv-footer.kv-footer-with-debug {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      align-items: stretch;
    }

    .mapping-kv-footer.kv-footer-with-debug .btn-add-row {
      flex: 1 1 12rem;
      min-width: 0;
      width: auto;
    }

    .btn-secondary {
      background-color: #334e68;
      color: white;
    }

    .btn-secondary:hover:not(:disabled) {
      background-color: #243b53;
    }

    .btn-outline {
      background: white;
      color: #334e68;
      border: 1px solid #bcccdc;
    }

    .btn-outline:hover:not(:disabled) {
      border-color: #2684ff;
      color: #2684ff;
    }

    .btn-run:disabled,
    .btn-secondary:disabled,
    .btn-outline:disabled,
    .btn-danger:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    .btn-danger {
      border: none;
      padding: 0.55rem 1.1rem;
      border-radius: 6px;
      font-weight: 500;
      font-size: 0.875rem;
      cursor: pointer;
      background: #dc2626;
      color: white;
      transition: background 0.2s;
    }

    .btn-danger:hover:not(:disabled) {
      background: #b91c1c;
    }

    button.danger {
      border: none;
      padding: 0.55rem 1.1rem;
      border-radius: 6px;
      font-weight: 500;
      font-size: 0.875rem;
      cursor: pointer;
      background: #dc2626;
      color: white;
      transition: background 0.2s;
    }

    button.danger:hover:not(:disabled) {
      background: #b91c1c;
    }

    button.danger:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    .field-hint {
      font-size: 0.75rem;
      color: #627d98;
      margin-top: 0.35rem;
      line-height: 1.45;
    }

    .field-hint code {
      font-size: 0.7rem;
      padding: 0.1rem 0.35rem;
      border-radius: 4px;
      background: #f0f4f8;
      color: #334e68;
    }

    .mapping-kv {
      border: 1px solid #e1e4e8;
      border-radius: 8px;
      overflow: hidden;
      background: #fbfcfd;
    }

    .mapping-kv-head,
    .mapping-kv-row {
      display: grid;
      grid-template-columns: minmax(5rem, 1fr) minmax(5rem, 1fr) minmax(6rem, 1.2fr) auto;
      gap: 0.5rem;
      align-items: center;
      padding: 0.5rem 0.65rem;
    }

    .mapping-kv-head.kv-cols-2,
    .mapping-kv-row.kv-cols-2 {
      grid-template-columns: minmax(6rem, 1fr) minmax(6rem, 1.2fr) auto;
    }

    @media (max-width: 640px) {
      .mapping-kv-head,
      .mapping-kv-row {
        grid-template-columns: 1fr;
      }

      .mapping-kv-head.kv-cols-2,
      .mapping-kv-row.kv-cols-2 {
        grid-template-columns: 1fr;
      }

      .mapping-kv-actions-h {
        display: none;
      }
    }

    .mapping-kv-head {
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: #627d98;
      background: #f0f4f8;
      border-bottom: 1px solid #e1e4e8;
    }

    .mapping-kv-head .mapping-kv-actions-h {
      text-align: center;
      min-width: 3.25rem;
    }

    .mapping-kv-row {
      background: white;
      border-bottom: 1px solid #f0f4f8;
    }

    .mapping-kv-row:last-of-type {
      border-bottom: none;
    }

    .mapping-kv-row input[type='text'] {
      min-width: 0;
      font-size: 0.8125rem;
      padding: 0.4rem 0.5rem;
    }

    .mapping-kv-remove {
      border: 1px solid #d9e2ec;
      background: white;
      color: #627d98;
      font-size: 0.75rem;
      padding: 0.35rem 0.5rem;
      border-radius: 6px;
      cursor: pointer;
      white-space: nowrap;
      transition: border-color 0.15s, color 0.15s, background 0.15s;
    }

    .mapping-kv-remove:hover:not(:disabled) {
      border-color: #bcccdc;
      color: #334e68;
      background: #f8fafc;
    }

    .mapping-kv-remove:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .mapping-kv-footer {
      padding: 0.5rem 0.65rem;
      background: #f8fafc;
      border-top: 1px solid #e1e4e8;
    }

    .btn-add-row {
      border: 1px dashed #bcccdc;
      background: white;
      color: #486581;
      font-size: 0.8125rem;
      font-weight: 500;
      padding: 0.45rem 0.75rem;
      border-radius: 6px;
      cursor: pointer;
      width: 100%;
      transition: border-color 0.15s, color 0.15s, background 0.15s;
    }

    .btn-add-row:hover {
      border-color: #2684ff;
      color: #2684ff;
      background: #f0f7ff;
    }

    .kv-stack {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .kv-block-title {
      font-size: 0.8125rem;
      font-weight: 600;
      color: #334e68;
      margin: 0 0 0.35rem;
    }

    .persist-hint {
      margin-top: 0.5rem;
      font-size: 0.8125rem;
      color: #047857;
      white-space: pre-line;
    }

    .persist-hint.warn {
      color: #b45309;
    }

    .persist-hint.err {
      color: #b91c1c;
    }

    .output-wrap {
      margin-top: 0.5rem;
    }

    .output-wrap label {
      margin-bottom: 0.35rem;
    }

    pre {
      margin: 0;
      padding: 1rem;
      background: #0f172a;
      color: #e2e8f0;
      border-radius: 6px;
      font-size: 0.75rem;
      line-height: 1.5;
      overflow: auto;
      max-height: 320px;
      white-space: pre-wrap;
      word-break: break-word;
    }

    pre.error {
      background: #450a0a;
      color: #fecaca;
    }

    pre.ok {
      background: #064e3b;
      color: #d1fae5;
    }
  `;

  @state() private vendorCode = '';
  @state() private uplinkProtocol = 'MQTT';
  @state() private downlinkProtocol = 'MQTT';
  @state() private connectionConfig = '{}';
  /** 解析模式：mapping=规则映射，js=高级脚本 */
  @state() private parseMode: 'mapping' | 'js' = 'js';
  @state() private networkMode: string = 'CLIENT';
  @state() private connParams: Record<string, string> = {};
  @state() private mappingRules: MappingRuleRow[] = [{ source: '', target: '', desc: '' }];
  @state() private downlinkRules: MappingRuleRow[] = [{ source: '', target: '', desc: '' }];
  @state() private upTestKV: TestKVRow[] = [{ key: '', value: '' }];
  @state() private downTestKV: TestKVRow[] = [{ key: '', value: '' }];
  @state() private uplinkJs = '';
  @state() private downlinkJs = '';
  @state() private rawPayload = '{\n  "id": "dev-001",\n  "temp": 23.5\n}';
  @state() private debugOutput = '';
  @state() private outputIsError = false;
  @state() private outputIsOk = false;
  @state() private saving = false;
  @state() private loadingConfig = false;
  @state() private persistHint = '';
  @state() private persistHintKind: 'info' | 'warn' | 'err' = 'info';
  @state() private adapterList: ProtocolScriptPayload[] = [];
  @state() private selectedListVendor: string | null = null;

  /** 标准物模型字段（datalist 联想，来自 /api/v1/fields） */
  @state() private dynamicProtoFields: Array<{ value: string; label: string }> = [];

  protected firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties);
    this.ensureGeekDefaultTemplate();
    this.ensureDownlinkDefaultTemplate();
    void this.fetchAdapterList();
    void this.fetchStandardFields();
  }

  private async fetchStandardFields() {
    try {
      const res = await request<StandardFieldPayload[]>('/api/v1/fields');
      if (res && Array.isArray(res)) {
        this.dynamicProtoFields = res.map((item) => ({
          value: item.field_name,
          label: `[${item.message_name ?? ''}] ${item.description} (${item.field_type})`,
        }));
      }
    } catch (err) {
      console.error('获取标准物模型失败:', err);
    }
  }

  private ensureGeekDefaultTemplate() {
    if (this.parseMode !== 'js') {
      return;
    }
    if (!this.uplinkJs.trim()) {
      this.uplinkJs = GEEK_DEFAULT_UPLINK_JS;
    }
  }

  private ensureDownlinkDefaultTemplate() {
    if (this.parseMode !== 'js') {
      return;
    }
    if (!this.downlinkJs.trim()) {
      this.downlinkJs = GEEK_DEFAULT_DOWNLINK_JS;
    }
  }

  private coerceMappingRuleRows(parsed: unknown): MappingRuleRow[] {
    if (!Array.isArray(parsed)) {
      return [{ source: '', target: '', desc: '' }];
    }
    const rows = parsed.map((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const o = item as Record<string, unknown>;
        return {
          source: String(o.source ?? ''),
          target: String(o.target ?? ''),
          desc: String(o.desc ?? ''),
        };
      }
      return { source: '', target: '', desc: '' };
    });
    return rows.length === 0 ? [{ source: '', target: '', desc: '' }] : rows;
  }

  /**
   * 从脚本注释中提取规则 JSON，兼容：
   * - 空数组 []
   * - 多行 JSON
   * - 关键字与冒号周围存在额外空格
   */
  private extractRulesFromScriptComment(script: string, tag: 'MAPPING_RULES' | 'DL_RULES'): string | null {
    if (!script.trim()) {
      return null;
    }
    const escapedTag = tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(String.raw`\/\*\s*${escapedTag}\s*:\s*([\s\S]*?)\s*\*\/`);
    const match = script.match(re);
    if (!match || match[1] === undefined) {
      return null;
    }
    return match[1].trim();
  }

  private syncConnParamsFromConfig() {
    try {
      const parsed: unknown = JSON.parse(this.connectionConfig || '{}');
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const obj = parsed as Record<string, unknown>;
        const next: Record<string, string> = {};
        for (const [k, v] of Object.entries(obj)) {
          next[k] =
            typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v ?? '');
        }
        this.connParams = next;
      } else {
        this.connParams = {};
      }
    } catch {
      this.connParams = {};
    }
  }

  /** 将 connectionConfig / 脚本注释 / 测试 JSON 同步到各 KV 表（含调试日志） */
  private syncStringToKV() {
    console.log('--- 开始解析数据到表单 ---');

    this.syncConnParamsFromConfig();

    const upRulesText = this.extractRulesFromScriptComment(this.uplinkJs, 'MAPPING_RULES');
    if (upRulesText !== null) {
      try {
        this.mappingRules = this.coerceMappingRuleRows(JSON.parse(upRulesText));
        console.log('上行映射解析成功:', this.mappingRules);
      } catch (err) {
        console.error('上行映射JSON解析失败:', err);
        this.mappingRules = [{ source: '', target: '', desc: '' }];
      }
    } else {
      console.warn('未找到 /* MAPPING_RULES: ... */ 注释，当前为纯JS脚本，无法还原上行表格。');
      this.mappingRules = [{ source: '', target: '', desc: '' }];
    }

    const dlRulesText = this.extractRulesFromScriptComment(this.downlinkJs, 'DL_RULES');
    if (dlRulesText !== null) {
      try {
        this.downlinkRules = this.coerceMappingRuleRows(JSON.parse(dlRulesText));
        console.log('下行映射解析成功:', this.downlinkRules);
      } catch (err) {
        console.error('下行映射JSON解析失败:', err);
        this.downlinkRules = [{ source: '', target: '', desc: '' }];
      }
    } else {
      console.warn('未找到 /* DL_RULES: ... */ 注释，无法还原下行表格。');
      this.downlinkRules = [{ source: '', target: '', desc: '' }];
    }

    try {
      if (this.rawPayload?.trim()) {
        const parsed: unknown = JSON.parse(this.rawPayload);
        if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const root = parsed as Record<string, unknown>;
          const upData = (root.uplink ?? root) as Record<string, unknown>;
          const downRaw = root.downlink;
          const downData =
            downRaw !== null && typeof downRaw === 'object' && !Array.isArray(downRaw)
              ? (downRaw as Record<string, unknown>)
              : {};
          const toRows = (rec: Record<string, unknown>): TestKVRow[] =>
            Object.keys(rec).map((k) => {
              const v = rec[k];
              return {
                key: k,
                value:
                  typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v ?? ''),
              };
            });
          this.upTestKV = toRows(upData);
          this.downTestKV = toRows(downData);
          if (this.upTestKV.length === 0) {
            this.upTestKV = [{ key: '', value: '' }];
          }
          if (this.downTestKV.length === 0) {
            this.downTestKV = [{ key: '', value: '' }];
          }
          console.log('测试报文解析成功:', { uplink: this.upTestKV, downlink: this.downTestKV });
        }
      } else {
        this.upTestKV = [{ key: '', value: '' }];
        this.downTestKV = [{ key: '', value: '' }];
      }
    } catch (err) {
      console.error('测试报文解析失败:', err);
    }

    console.log('--- 解析结束 ---');
  }

  /** 解析模式下拉切换：与 connectionConfig / 脚本 / 测试报文字符串双向同步 */
  private handleModeChange = (e: Event) => {
    const select = e.target as HTMLSelectElement;
    const mode = select.value as 'mapping' | 'js';
    this.parseMode = mode;

    if (this.parseMode === 'mapping') {
      this.syncStringToKV();
      return;
    }

    try {
      this.connectionConfig = JSON.stringify(this.connParams);
      this.rawPayload = this.mergeRawPayloadFromTestKVs();
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
      this.parseMode = 'mapping';
      return;
    }
    try {
      this.uplinkJs = this.buildUplinkJsFromMappingRows();
    } catch {
      if (!this.uplinkJs.trim()) {
        alert('请至少添加一条有效的上行字段映射后再切换为高级脚本。');
        this.parseMode = 'mapping';
        return;
      }
    }
    this.downlinkJs = this.buildDownlinkJsFromDownlinkRules();
    this.ensureGeekDefaultTemplate();
    this.ensureDownlinkDefaultTemplate();
  };

  /** 规则映射行生成 uplink_js（保存 / 调试前调用）：深层路径 getVal、$fixed:、$timestamp、核心报文头补全 */
  private buildUplinkJsFromMappingRows(): string {
    const validRules = this.mappingRules.filter(
      (r) => r.source.trim() !== '' && r.target.trim() !== ''
    );
    if (validRules.length === 0) {
      throw new Error('请至少添加一条有效的字段映射（原始字段与标准字段均需填写）');
    }
    const rulesPayload = validRules.map((r) => ({
      source: r.source.trim(),
      target: r.target.trim(),
      desc: r.desc.trim(),
    }));
    const rulesStr = JSON.stringify(rulesPayload);

    return `/* MAPPING_RULES: ${rulesStr} */
function parseUplink(raw) {
    if (!raw) return null;

    // 深层字段解析函数 (支持 data.power 这种格式)
    function getVal(obj, path) {
        return path.split('.').reduce((acc, part) => acc && acc[part] !== undefined ? acc[part] : undefined, obj);
    }

    // 1. 自动补全：底层系统级必填字段 (兜底逻辑)
    let res = {
        robot_type: 0,
        type: 2,
        code: 1,
        priority: 1,
        timestamp: Date.now(),
        status_data: {},
        location_data: {}
    };

    // 2. 动态提取：用户在 UI 配置的映射规则
    const rules = ${rulesStr};
    rules.forEach(r => {
        let val;
        // 支持固定值和时间戳伪指令
        if (r.source.startsWith('$fixed:')) {
            const strVal = r.source.substring(7);
            val = isNaN(Number(strVal)) ? strVal : Number(strVal);
        } else if (r.source === '$timestamp') {
            val = raw.msgId ? parseInt(raw.msgId) : Date.now();
        } else {
            // 使用深层解析读取真实数据
            val = getVal(raw, r.source);
        }

        if (val !== undefined) {
            // 核心标识字段
            if (r.target === 'sn' || r.target === 'device_id') {
                res.sn = String(val);
                res.nats_subject = "v1.robot.telemetry." + res.sn;
            }
            // 基础控制与核心报文头覆盖
            else if (['robot_type', 'type', 'sequence', 'code', 'timestamp', 'session_id', 'priority'].includes(r.target)) {
                res[r.target] = val;
            }
            // 位置负载分类
            else if (['x', 'y', 'z', 'toward'].includes(r.target)) {
                res.location_data[r.target] = val;
            }
            // 其他默认归入状态负载
            else {
                res.status_data[r.target] = val;
            }
        }
    });

    if (!res.sn) return null;
    return res;
}`;
  }

  private buildDownlinkJsFromDownlinkRules(): string {
    const validDownlinkRules = this.downlinkRules.filter(
      (r) => r.source.trim() !== '' && r.target.trim() !== ''
    );
    const rulesPayload = validDownlinkRules.map((r) => ({
      source: r.source.trim(),
      target: r.target.trim(),
      desc: r.desc.trim(),
    }));
    const dlStr = JSON.stringify(rulesPayload);
    return `/* DL_RULES: ${dlStr} */
function buildDownlink(raw) {
    let payload = {};
    const rules = ${dlStr};
    rules.forEach(r => { if (raw[r.source] !== undefined) payload[r.target] = raw[r.source]; });
    return { topic: "default/topic", payload: JSON.stringify(payload) }; // 可由用户在代码中微调
}`;
  }

  /** 将上行/下行测试 KV 合并为 { uplink, downlink } JSON 字符串 */
  private mergeRawPayloadFromTestKVs(): string {
    const upObj: Record<string, unknown> = {};
    for (const item of this.upTestKV) {
      const k = item.key.trim();
      if (!k) {
        continue;
      }
      const v = item.value.trim();
      if (v === '') {
        upObj[k] = '';
        continue;
      }
      try {
        upObj[k] = JSON.parse(v) as unknown;
      } catch {
        upObj[k] = v;
      }
    }
    const downObj: Record<string, unknown> = {};
    for (const item of this.downTestKV) {
      const k = item.key.trim();
      if (!k) {
        continue;
      }
      const v = item.value.trim();
      if (v === '') {
        downObj[k] = '';
        continue;
      }
      try {
        downObj[k] = JSON.parse(v) as unknown;
      } catch {
        downObj[k] = v;
      }
    }
    return JSON.stringify({ uplink: upObj, downlink: downObj }, null, 2);
  }

  /** 接口 / 列表选中后写入字符串字段并按上行脚本是否含 MAPPING_RULES 切换模式与表格 */
  private applyLoadedProtocolData(data: ProtocolScriptPayload, okHint: string) {
    this.networkMode = (data.network_mode ?? '').trim() || 'CLIENT';
    this.connectionConfig = data.connection_config ?? '';
    this.syncConnParamsFromConfig();
    this.uplinkJs = data.uplink_js ?? '';
    this.downlinkJs = data.downlink_js ?? '';

    // 仅当上下行规则都可成功解析时进入 mapping；否则保留原始脚本并显示 JS 模式。
    const upRulesText = this.extractRulesFromScriptComment(this.uplinkJs, 'MAPPING_RULES');
    const dlRulesText = this.extractRulesFromScriptComment(this.downlinkJs, 'DL_RULES');
    let canUseMapping = false;
    if (upRulesText !== null && dlRulesText !== null) {
      try {
        this.mappingRules = this.coerceMappingRuleRows(JSON.parse(upRulesText));
        this.downlinkRules = this.coerceMappingRuleRows(JSON.parse(dlRulesText));
        canUseMapping = true;
      } catch (err) {
        console.warn('规则注释解析失败，回退到高级脚本模式以保留原始代码回显:', err);
      }
    }

    this.parseMode = canUseMapping ? 'mapping' : 'js';
    if (this.parseMode === 'mapping') {
      this.syncStringToKV();
    } else {
      // 注意：这里绝不覆盖后端返回的原始脚本内容，仅在后端返回空串时才填默认模板。
      if (!this.uplinkJs.trim()) {
        this.ensureGeekDefaultTemplate();
      }
      if (!this.downlinkJs.trim()) {
        this.ensureDownlinkDefaultTemplate();
      }
    }
    this.setPersistHint(okHint, 'info');
  }

  private updateMappingRow(index: number, field: keyof MappingRuleRow, value: string) {
    const next = [...this.mappingRules];
    const row = next[index];
    if (!row) {
      return;
    }
    next[index] = { ...row, [field]: value };
    this.mappingRules = next;
  }

  private addMappingRow() {
    this.mappingRules = [...this.mappingRules, { source: '', target: '', desc: '' }];
  }

  private removeMappingRow(index: number) {
    if (this.mappingRules.length <= 1) {
      return;
    }
    this.mappingRules = this.mappingRules.filter((_, i) => i !== index);
  }

  private updateConnParam(key: string, value: string) {
    this.connParams = { ...this.connParams, [key]: value };
  }

  private updateDownlinkRow(index: number, field: keyof MappingRuleRow, value: string) {
    const next = [...this.downlinkRules];
    const row = next[index];
    if (!row) {
      return;
    }
    next[index] = { ...row, [field]: value };
    this.downlinkRules = next;
  }

  private addDownlinkRow() {
    this.downlinkRules = [...this.downlinkRules, { source: '', target: '', desc: '' }];
  }

  private removeDownlinkRow(index: number) {
    if (this.downlinkRules.length <= 1) {
      return;
    }
    this.downlinkRules = this.downlinkRules.filter((_, i) => i !== index);
  }

  private updateUpTestKVRow(index: number, field: keyof TestKVRow, value: string) {
    const next = [...this.upTestKV];
    const row = next[index];
    if (!row) {
      return;
    }
    next[index] = { ...row, [field]: value };
    this.upTestKV = next;
  }

  private addUpTestKVRow() {
    this.upTestKV = [...this.upTestKV, { key: '', value: '' }];
  }

  private removeUpTestKVRow(index: number) {
    if (this.upTestKV.length <= 1) {
      return;
    }
    this.upTestKV = this.upTestKV.filter((_, i) => i !== index);
  }

  private updateDownTestKVRow(index: number, field: keyof TestKVRow, value: string) {
    const next = [...this.downTestKV];
    const row = next[index];
    if (!row) {
      return;
    }
    next[index] = { ...row, [field]: value };
    this.downTestKV = next;
  }

  private addDownTestKVRow() {
    this.downTestKV = [...this.downTestKV, { key: '', value: '' }];
  }

  private removeDownTestKVRow(index: number) {
    if (this.downTestKV.length <= 1) {
      return;
    }
    this.downTestKV = this.downTestKV.filter((_, i) => i !== index);
  }

  private createNew() {
    this.selectedListVendor = null;
    this.vendorCode = '';
    this.uplinkProtocol = 'MQTT';
    this.downlinkProtocol = 'MQTT';
    this.networkMode = 'CLIENT';
    this.parseMode = 'mapping';
    this.connParams = {};
    this.mappingRules = [{ source: '', target: '', desc: '' }];
    this.downlinkRules = [{ source: '', target: '', desc: '' }];
    this.upTestKV = [{ key: '', value: '' }];
    this.downTestKV = [{ key: '', value: '' }];
    this.debugOutput = '';
    this.uplinkJs = '';
    this.downlinkJs = '';
    this.connectionConfig = '';
    this.rawPayload = '';
    this.outputIsError = false;
    this.outputIsOk = false;
    this.setPersistHint('新建协议：填写厂商标识与连接参数后保存', 'info');
  }

  private deleteConfig = async () => {
    const v = this.vendorCode.trim();
    if (!v) {
      alert('当前没有加载任何协议，无法删除');
      return;
    }
    if (!confirm(`确定要彻底删除适配器 [${v}] 吗？此操作不可恢复！`)) {
      return;
    }
    try {
      await request<unknown>(`/api/v1/scripts/${encodeURIComponent(v)}`, { method: 'DELETE' });
      alert('删除成功');
      this.createNew();
      await this.fetchAdapterList();
      this.setPersistHint('已删除该协议', 'info');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      alert(`删除失败: ${msg}`);
    }
  };

  private setPersistHint(msg: string, kind: 'info' | 'warn' | 'err' = 'info') {
    this.persistHint = msg;
    this.persistHintKind = kind;
  }

  private async fetchAdapterList() {
    try {
      const res = await request<ProtocolScriptPayload[]>('/api/v1/scripts', { method: 'GET' });
      if (Array.isArray(res)) {
        this.adapterList = res;
      }
    } catch (err) {
      console.error('获取协议列表失败:', err);
    }
  }

  private applyFromListItem(item: ProtocolScriptPayload) {
    const vendor = (item.vendor_code || '').trim();
    if (!vendor) {
      return;
    }
    this.selectedListVendor = vendor;
    this.vendorCode = vendor;
    void this.loadVendorFromApi(vendor, '已从列表加载（实时）');
  }

  /** 列表点击与手动加载统一走详情接口，避免使用列表缓存导致回显旧数据 */
  private async loadVendorFromApi(vendorCode: string, okHint: string) {
    const v = vendorCode.trim();
    if (!v) {
      this.setPersistHint('厂商标识不能为空', 'warn');
      return;
    }
    this.loadingConfig = true;
    this.setPersistHint('');
    try {
      const data = await request<ProtocolScriptPayload>(
        `/api/v1/scripts/${encodeURIComponent(v)}`,
        { method: 'GET' }
      );
      this.selectedListVendor = data.vendor_code || v;
      this.vendorCode = data.vendor_code || v;
      this.uplinkProtocol = data.uplink_protocol || 'MQTT';
      this.downlinkProtocol = data.downlink_protocol || 'MQTT';
      this.applyLoadedProtocolData(data, okHint);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      const is404 = msg.includes('404');
      this.setPersistHint(is404 ? '未找到该厂商配置' : `加载失败: ${msg}`, is404 ? 'warn' : 'err');
    } finally {
      this.loadingConfig = false;
    }
  }

  private debugUplink = async () => {
    const payloadObj: Record<string, string> = {};
    this.upTestKV
      .filter((item) => item.key.trim())
      .forEach((item) => {
        payloadObj[item.key.trim()] = item.value;
      });

    if (this.parseMode === 'mapping') {
      try {
        this.uplinkJs = this.buildUplinkJsFromMappingRows();
      } catch {
        /* 保留当前 uplinkJs */
      }
    }

    try {
      const res = await request<unknown>('/api/v1/scripts/debug', {
        method: 'POST',
        body: JSON.stringify({
          script: this.uplinkJs,
          funcName: 'parseUplink',
          raw_payload: payloadObj,
        }),
      });
      this.debugOutput = JSON.stringify(
        {
          '【上行调试】模拟设备发来的原始报文': payloadObj,
          '【执行结果】准备发往 NATS 的标准数据': res,
        },
        null,
        2
      );
      this.outputIsError = false;
      this.outputIsOk = false;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.debugOutput = `上行调试失败:\n${msg}`;
      this.outputIsError = true;
      this.outputIsOk = false;
    }
  };

  private debugDownlink = async () => {
    const payloadObj: Record<string, string> = {};
    this.downTestKV
      .filter((item) => item.key.trim())
      .forEach((item) => {
        payloadObj[item.key.trim()] = item.value;
      });

    if (this.parseMode === 'mapping') {
      const dlStr = JSON.stringify(this.downlinkRules.filter((r) => r.source && r.target));
      this.downlinkJs = `/* DL_RULES: ${dlStr} */\nfunction buildDownlink(raw) { let payload = {}; const rules = ${dlStr}; rules.forEach(r => { if (raw[r.source] !== undefined) payload[r.target] = raw[r.source]; }); return { topic: "robot/cmd/" + (raw.sn || "unknown"), payload: JSON.stringify(payload) }; }`;
    }

    try {
      const res = await request<unknown>('/api/v1/scripts/debug', {
        method: 'POST',
        body: JSON.stringify({
          script: this.downlinkJs,
          funcName: 'buildDownlink',
          raw_payload: payloadObj,
        }),
      });
      this.debugOutput = JSON.stringify(
        {
          '【下行调试】模拟 NATS 发来的标准指令': payloadObj,
          '【执行结果】准备下发给物理设备的方言': res,
        },
        null,
        2
      );
      this.outputIsError = false;
      this.outputIsOk = false;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.debugOutput = `下行调试失败:\n${msg}`;
      this.outputIsError = true;
      this.outputIsOk = false;
    }
  };

  private async saveConfig() {
    const v = this.vendorCode.trim();
    if (!v) {
      this.setPersistHint('厂商标识不能为空', 'warn');
      return;
    }
    this.saving = true;
    this.setPersistHint('');
    try {
      this.connectionConfig = JSON.stringify(this.connParams);
      let uplinkJsToSave = this.uplinkJs;
      let connectionStr = this.connectionConfig;
      let downlinkStr = this.downlinkJs ?? '';
      if (this.parseMode === 'mapping') {
        try {
          uplinkJsToSave = this.buildUplinkJsFromMappingRows();
          connectionStr = JSON.stringify(this.connParams);
          downlinkStr = this.buildDownlinkJsFromDownlinkRules();
          const upObj: Record<string, string> = {};
          this.upTestKV
            .filter((item) => item.key.trim())
            .forEach((item) => {
              upObj[item.key.trim()] = item.value;
            });
          const downObj: Record<string, string> = {};
          this.downTestKV
            .filter((item) => item.key.trim())
            .forEach((item) => {
              downObj[item.key.trim()] = item.value;
            });
          this.rawPayload = JSON.stringify({ uplink: upObj, downlink: downObj });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          alert(msg);
          this.setPersistHint(msg, 'warn');
          this.saving = false;
          return;
        }
      }
      const body: ProtocolScriptPayload = {
        vendor_code: v,
        uplink_protocol: this.uplinkProtocol,
        downlink_protocol: this.downlinkProtocol,
        network_mode: this.networkMode,
        connection_config: connectionStr,
        uplink_js: uplinkJsToSave,
        downlink_js: downlinkStr,
      };
      await request<unknown>('/api/v1/scripts', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (this.parseMode === 'mapping') {
        this.connectionConfig = connectionStr;
        this.uplinkJs = uplinkJsToSave;
        this.downlinkJs = downlinkStr;
      }
      this.debugOutput = '保存成功';
      this.outputIsError = false;
      this.outputIsOk = true;
      this.setPersistHint('配置已保存', 'info');
      this.selectedListVendor = v;
      await this.fetchAdapterList();
      await this.loadVendorFromApi(v, '配置已保存并已同步最新数据');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.debugOutput = msg;
      this.outputIsError = true;
      this.outputIsOk = false;
      this.setPersistHint('保存失败', 'err');
    } finally {
      this.saving = false;
    }
  }

  private async loadConfig() {
    const v = this.vendorCode.trim();
    await this.loadVendorFromApi(v, '已加载该厂商配置');
  }

  private renderConnField(
    key: string,
    label: string,
    placeholder: string,
    inputType: 'text' | 'password' = 'text'
  ) {
    return html`
      <div class="field">
        <label>${label}</label>
        <input
          type=${inputType}
          .value=${this.connParams[key] ?? ''}
          placeholder=${placeholder}
          @input=${(e: Event) =>
            this.updateConnParam(key, (e.target as HTMLInputElement).value)}
        />
      </div>
    `;
  }

  private renderConnTextarea(key: string, label: string, placeholder: string, rows = 3) {
    return html`
      <div class="field">
        <label>${label}</label>
        <textarea
          rows=${rows}
          spellcheck="false"
          .value=${this.connParams[key] ?? ''}
          placeholder=${placeholder}
          @input=${(e: Event) =>
            this.updateConnParam(key, (e.target as HTMLTextAreaElement).value)}
        ></textarea>
      </div>
    `;
  }

  private renderConnectionConfigFields() {
    const up = (this.uplinkProtocol || 'MQTT').toUpperCase();
    if (this.networkMode === 'SERVER') {
      const pathHint =
        up === 'MQTT'
          ? 'MQTT 上行主题过滤器，如 robot/status/up/#（与内嵌 Broker 命中规则一致）'
          : '例如 /ws 或 device/+/telemetry';
      return html`
        ${this.renderConnField('listen_port', '本地监听端口 (listen_port)', '例如 1883（内嵌 MQTT Broker 绑定 :端口）')}
        ${this.renderConnField('path_topic', '接口路径/主题前缀 (path_topic)', pathHint)}
        ${this.renderConnField('keepalive', '心跳超时秒数 (keepalive)', '例如 60')}
      `;
    }
    if (up === 'MQTT') {
      return html`
        ${this.renderConnField(
          'server_address',
          'Broker 地址 (server_address)',
          'tcp://host:1883 或 mqtts://...'
        )}
        ${this.renderConnField('clientId', 'Client ID (clientId)', '适配器唯一客户端标识')}
        ${this.renderConnField('username', '用户名 (username)', '可选')}
        ${this.renderConnField('password', '密码 (password)', '可选', 'password')}
        ${this.renderConnField('subTopic', '订阅主题 (subTopic)', '例如 robot/status/up/#')}
        ${this.renderConnField('pubTopic', '发布主题 (pubTopic)', '例如 robot/cmd/down/')}
      `;
    }
    if (up === 'WEBSOCKET') {
      return html`
        ${this.renderConnField(
          'server_address',
          '目标地址 (server_address)',
          'ws://host:8080/path 或 wss://...'
        )}
        ${this.renderConnTextarea(
          'headers',
          '鉴权头 JSON (headers)',
          '{"Authorization":"Bearer ..."}'
        )}
      `;
    }
    return html`
      ${this.renderConnField(
        'server_address',
        'API 基地址 (server_address)',
        'https://api.example.com'
      )}
      ${this.renderConnField('path_topic', '接口路径 (path_topic)', '例如 /v1/report')}
      ${this.renderConnTextarea('headers', '请求头 JSON (headers)', '{}', 2)}
    `;
  }

  private renderSidebar() {
    return html`
      <aside class="panel sidebar" aria-label="已保存协议列表">
        <div class="sidebar-head">
          <h2 class="sidebar-title">已配置的适配器</h2>
          <button type="button" class="btn-new" @click=${() => this.createNew()}>+ 新建协议</button>
        </div>
        ${this.adapterList.length === 0
          ? html`<p class="sidebar-empty">暂无已保存配置</p>`
          : html`
              <ul class="sidebar-list">
                ${this.adapterList.map(
                  (item) => html`
                    <li class="sidebar-item">
                      <button
                        type="button"
                        class="sidebar-link ${this.selectedListVendor === item.vendor_code
                          ? 'active'
                          : ''}"
                        @click=${() => this.applyFromListItem(item)}
                      >
                        <span class="sidebar-vendor">${item.vendor_code}</span>
                        <span class="sidebar-proto"
                          >(${item.uplink_protocol || '—'})</span
                        >
                      </button>
                    </li>
                  `
                )}
              </ul>
            `}
      </aside>
    `;
  }

  render() {
    const hintClass =
      this.persistHintKind === 'err' ? 'err' : this.persistHintKind === 'warn' ? 'warn' : '';
    const preClass = [
      this.outputIsError ? 'error' : '',
      this.outputIsOk ? 'ok' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return html`
      <div class="page-wrap">
        ${this.renderSidebar()}
        <div class="main-pair">
          <section class="panel" aria-labelledby="conn-config-title">
            <h2 id="conn-config-title">连接配置</h2>
            <div class="field">
              <label for="vendor">厂商标识 (Vendor Code)</label>
              <div class="field-row">
                <div class="field-grow">
                  <input
                    id="vendor"
                    type="text"
                    .value=${this.vendorCode}
                    @input=${(e: Event) => {
                      this.vendorCode = (e.target as HTMLInputElement).value;
                    }}
                    placeholder="例如 wanwei"
                  />
                </div>
                <button
                  type="button"
                  class="btn-outline"
                  ?disabled=${this.loadingConfig}
                  @click=${() => this.loadConfig()}
                >
                  ${this.loadingConfig ? '加载中…' : '加载已有配置'}
                </button>
              </div>
            </div>
            <div class="field">
              <label for="uplink-proto">上行协议 (Uplink Protocol)</label>
              <select
                id="uplink-proto"
                .value=${this.uplinkProtocol}
                @change=${(e: Event) => {
                  this.uplinkProtocol = (e.target as HTMLSelectElement).value;
                }}
              >
                <option value="MQTT">MQTT</option>
                <option value="WEBSOCKET">WebSocket</option>
                <option value="HTTP">HTTP</option>
              </select>
            </div>
            <div class="field">
              <label for="network-mode">网络模式 (Network Mode)</label>
              <select
                id="network-mode"
                .value=${this.networkMode}
                @change=${(e: Event) => {
                  this.networkMode = (e.target as HTMLSelectElement).value;
                }}
              >
                <option value="CLIENT">作为客户端 (桥接第三方平台/设备)</option>
                <option value="SERVER">作为服务端 (等待设备主动直连)</option>
              </select>
            </div>
            <div class="field">
              <label for="downlink-proto">下行协议 (Downlink Protocol)</label>
              <select
                id="downlink-proto"
                .value=${this.downlinkProtocol}
                @change=${(e: Event) => {
                  this.downlinkProtocol = (e.target as HTMLSelectElement).value;
                }}
              >
                <option value="MQTT">MQTT</option>
                <option value="WEBSOCKET">WebSocket</option>
                <option value="HTTP">HTTP</option>
              </select>
            </div>
            <div class="field" role="group" aria-label="连接参数固定表单">
              <div class="kv-block-title">连接参数 (Connection Config)</div>
              ${this.renderConnectionConfigFields()}
            </div>
            <div class="field">
              <label for="parse-mode">解析模式 (Parse Mode)</label>
              <select
                id="parse-mode"
                .value=${this.parseMode}
                @change=${this.handleModeChange}
              >
                <option value="mapping">规则映射</option>
                <option value="js">高级脚本</option>
              </select>
            </div>
            <div class="actions">
              <button
                type="button"
                class="btn-secondary"
                ?disabled=${this.saving}
                @click=${() => this.saveConfig()}
              >
                ${this.saving ? '保存中…' : '保存配置'}
              </button>
            </div>
            ${this.persistHint
              ? html`<div class="persist-hint ${hintClass}">${this.persistHint}</div>`
              : null}
          </section>

          <section class="panel" aria-labelledby="params-config-title">
            <h2 id="params-config-title">连接参数配置</h2>
            ${this.parseMode === 'mapping'
              ? html`
                  <div class="kv-stack">
                    <div class="field">
                      <div class="kv-block-title">上行字段映射</div>
                      <div class="mapping-kv" role="group" aria-label="上行字段映射表">
                        <div class="mapping-kv-head">
                          <span>原始字段 (Source)</span>
                          <span>标准字段 (Target)</span>
                          <span>字段说明 (Description)</span>
                          <span class="mapping-kv-actions-h">操作</span>
                        </div>
                        ${this.mappingRules.map(
                          (row, i) => html`
                            <div class="mapping-kv-row">
                              <input
                                type="text"
                                .value=${row.source}
                                placeholder="如 device_id"
                                aria-label="原始字段"
                                @input=${(e: Event) =>
                                  this.updateMappingRow(
                                    i,
                                    'source',
                                    (e.target as HTMLInputElement).value
                                  )}
                              />
                              <input
                                type="text"
                                list="proto-fields-list"
                                .value=${row.target}
                                placeholder="如 sn、power"
                                aria-label="标准字段"
                                @input=${(e: Event) =>
                                  this.updateMappingRow(
                                    i,
                                    'target',
                                    (e.target as HTMLInputElement).value
                                  )}
                              />
                              <input
                                type="text"
                                .value=${row.desc}
                                placeholder="可选"
                                aria-label="字段说明"
                                @input=${(e: Event) =>
                                  this.updateMappingRow(
                                    i,
                                    'desc',
                                    (e.target as HTMLInputElement).value
                                  )}
                              />
                              <button
                                type="button"
                                class="mapping-kv-remove"
                                ?disabled=${this.mappingRules.length <= 1}
                                @click=${() => this.removeMappingRow(i)}
                              >
                                删除
                              </button>
                            </div>
                          `
                        )}
                        <div class="mapping-kv-footer">
                          <button
                            type="button"
                            class="btn-add-row"
                            @click=${() => this.addMappingRow()}
                          >
                            + 添加字段映射
                          </button>
                        </div>
                      </div>
                      <p class="field-hint">
                        标准字段为 <code>sn</code> 或 <code>device_id</code> 时用于设备标识；保存时将生成上行解析脚本。
                      </p>
                    </div>
                    <div class="field">
                      <div class="kv-block-title">下行字段映射</div>
                      <div class="mapping-kv" role="group" aria-label="下行字段映射表">
                        <div class="mapping-kv-head">
                          <span>标准字段 (Source)</span>
                          <span>设备字段 (Target)</span>
                          <span>字段说明 (Description)</span>
                          <span class="mapping-kv-actions-h">操作</span>
                        </div>
                        ${this.downlinkRules.map(
                          (row, i) => html`
                            <div class="mapping-kv-row">
                              <input
                                type="text"
                                list="proto-fields-list"
                                .value=${row.source}
                                placeholder="如 cmd"
                                aria-label="标准字段"
                                @input=${(e: Event) =>
                                  this.updateDownlinkRow(
                                    i,
                                    'source',
                                    (e.target as HTMLInputElement).value
                                  )}
                              />
                              <input
                                type="text"
                                .value=${row.target}
                                placeholder="如 action"
                                aria-label="设备字段"
                                @input=${(e: Event) =>
                                  this.updateDownlinkRow(
                                    i,
                                    'target',
                                    (e.target as HTMLInputElement).value
                                  )}
                              />
                              <input
                                type="text"
                                .value=${row.desc}
                                placeholder="可选"
                                aria-label="字段说明"
                                @input=${(e: Event) =>
                                  this.updateDownlinkRow(
                                    i,
                                    'desc',
                                    (e.target as HTMLInputElement).value
                                  )}
                              />
                              <button
                                type="button"
                                class="mapping-kv-remove"
                                ?disabled=${this.downlinkRules.length <= 1}
                                @click=${() => this.removeDownlinkRow(i)}
                              >
                                删除
                              </button>
                            </div>
                          `
                        )}
                        <div class="mapping-kv-footer">
                          <button
                            type="button"
                            class="btn-add-row"
                            @click=${() => this.addDownlinkRow()}
                          >
                            + 添加下行映射
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="field">
                      <div class="kv-block-title">上行测试报文 (Uplink Payload)</div>
                      <div class="mapping-kv" role="group" aria-label="上行测试键值表">
                        <div class="mapping-kv-head kv-cols-2">
                          <span>测试键 (Key)</span>
                          <span>测试值 (Value)</span>
                          <span class="mapping-kv-actions-h">操作</span>
                        </div>
                        ${this.upTestKV.map(
                          (row, i) => html`
                            <div class="mapping-kv-row kv-cols-2">
                              <input
                                type="text"
                                .value=${row.key}
                                placeholder="键"
                                aria-label="上行测试键"
                                @input=${(e: Event) =>
                                  this.updateUpTestKVRow(i, 'key', (e.target as HTMLInputElement).value)}
                              />
                              <input
                                type="text"
                                .value=${row.value}
                                placeholder="值或 JSON"
                                aria-label="上行测试值"
                                @input=${(e: Event) =>
                                  this.updateUpTestKVRow(
                                    i,
                                    'value',
                                    (e.target as HTMLInputElement).value
                                  )}
                              />
                              <button
                                type="button"
                                class="mapping-kv-remove"
                                ?disabled=${this.upTestKV.length <= 1}
                                @click=${() => this.removeUpTestKVRow(i)}
                              >
                                删除
                              </button>
                            </div>
                          `
                        )}
                        <div class="mapping-kv-footer kv-footer-with-debug">
                          <button type="button" class="btn-add-row" @click=${() => this.addUpTestKVRow()}>
                            + 添加上行字段
                          </button>
                          <button type="button" class="btn-run primary" @click=${this.debugUplink}>
                            调试上行解析
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="field">
                      <div class="kv-block-title">下行测试报文 (Downlink Payload)</div>
                      <div class="mapping-kv" role="group" aria-label="下行测试键值表">
                        <div class="mapping-kv-head kv-cols-2">
                          <span>测试键 (Key)</span>
                          <span>测试值 (Value)</span>
                          <span class="mapping-kv-actions-h">操作</span>
                        </div>
                        ${this.downTestKV.map(
                          (row, i) => html`
                            <div class="mapping-kv-row kv-cols-2">
                              <input
                                type="text"
                                .value=${row.key}
                                placeholder="键"
                                aria-label="下行测试键"
                                @input=${(e: Event) =>
                                  this.updateDownTestKVRow(i, 'key', (e.target as HTMLInputElement).value)}
                              />
                              <input
                                type="text"
                                .value=${row.value}
                                placeholder="值或 JSON"
                                aria-label="下行测试值"
                                @input=${(e: Event) =>
                                  this.updateDownTestKVRow(
                                    i,
                                    'value',
                                    (e.target as HTMLInputElement).value
                                  )}
                              />
                              <button
                                type="button"
                                class="mapping-kv-remove"
                                ?disabled=${this.downTestKV.length <= 1}
                                @click=${() => this.removeDownTestKVRow(i)}
                              >
                                删除
                              </button>
                            </div>
                          `
                        )}
                        <div class="mapping-kv-footer kv-footer-with-debug">
                          <button
                            type="button"
                            class="btn-add-row"
                            @click=${() => this.addDownTestKVRow()}
                          >
                            + 添加下行字段
                          </button>
                          <button type="button" class="btn-run primary" @click=${this.debugDownlink}>
                            调试下行构建
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                `
              : html`
                  <div class="field">
                    <label for="uplink-js">上行解析脚本 (Uplink JS)</label>
                    <textarea
                      id="uplink-js"
                      class="script-area"
                      .value=${this.uplinkJs}
                      @input=${(e: Event) => {
                        this.uplinkJs = (e.target as HTMLTextAreaElement).value;
                      }}
                      spellcheck="false"
                    ></textarea>
                  </div>
                  <div class="field">
                    <label for="downlink-js">下行构建脚本 (Downlink JS)</label>
                    <textarea
                      id="downlink-js"
                      class="script-area"
                      .value=${this.downlinkJs}
                      @input=${(e: Event) => {
                        this.downlinkJs = (e.target as HTMLTextAreaElement).value;
                      }}
                      spellcheck="false"
                      placeholder="可选，保存时会一并写入后端"
                    ></textarea>
                  </div>
                  <div class="field">
                    <label for="raw-payload">测试报文 JSON（含 uplink / downlink 对象，兼容扁平结构）</label>
                    <textarea
                      id="raw-payload"
                      class="payload-area"
                      .value=${this.rawPayload}
                      @input=${(e: Event) => {
                        this.rawPayload = (e.target as HTMLTextAreaElement).value;
                      }}
                      spellcheck="false"
                    ></textarea>
                  </div>
                `}
            <div class="actions">
              <button
                type="button"
                class="btn-outline"
                ?disabled=${this.saving}
                @click=${() => this.saveConfig()}
              >
                ${this.saving ? '保存中…' : '保存配置'}
              </button>
              <button type="button" class="danger" ?disabled=${this.saving} @click=${this.deleteConfig}>
                删除协议
              </button>
            </div>
            <div class="output-wrap">
              <label>调试结果输出</label>
              <pre class=${preClass}>${this.debugOutput || '（暂无输出）'}</pre>
            </div>
          </section>
        </div>
        <datalist id="proto-fields-list">
          ${this.dynamicProtoFields.map((f) => html`<option value=${f.value}>${f.label}</option>`)}
        </datalist>
      </div>
    `;
  }
}
